/**
 * AgentThinkingAnimation — 8-bit style AI agent activity visualization
 *
 * Subscribes to REAL progress events from avengers.js via Supabase Realtime.
 * Shows pixel-art styled agents working through the pipeline stages.
 * Falls back to ambient filler animations between real events.
 *
 * Created: 2026-02-27 by Douglas + Claude
 * Last edited: 2026-02-27 by Douglas — wired to real Realtime progress events
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';

// Agent characters — the ODV Avengers in pixel form
const AGENTS = [
  { name: 'Yoda', emoji: '🧘', color: '#9333ea', role: 'Orchestrator' },
  { name: 'Parker', emoji: '🕷️', color: '#ef4444', role: 'Field Ops' },
  { name: 'Marty', emoji: '⚡', color: '#3b82f6', role: 'Finance' },
  { name: 'Buddha', emoji: '☸️', color: '#10b981', role: 'Guardian' },
  { name: 'Logic', emoji: '🧱', color: '#f59e0b', role: 'Builder' },
  { name: 'Gatsby', emoji: '🎩', color: '#ec4899', role: 'Client Relations' },
  { name: 'Sherlock', emoji: '🔍', color: '#6366f1', role: 'Investigator' },
];

// Map pipeline steps to visual phases
const STEP_TO_PHASE = {
  routing: 'comms',
  routed: 'comms',
  claimed: 'comms',
  loading_bricks: 'db',
  spawning_claude: 'process',
  claude_working: 'process',
  response_received: 'process',
};

// Map pipeline steps to icons
const STEP_ICONS = {
  routing: '📡',
  routed: '🎯',
  claimed: '✋',
  loading_bricks: '🧱',
  spawning_claude: '🧠',
  claude_working: '⚙️',
  response_received: '✅',
};

// Ambient filler activities — shown between real progress events
const FILLER_ACTIVITIES = [
  { agent: 'Logic', action: 'indexing domain context', icon: '📚', phase: 'db' },
  { agent: 'Logic', action: 'preparing SQL queries', icon: '🗄️', phase: 'db' },
  { agent: 'Logic', action: 'loading table schemas', icon: '📊', phase: 'db' },
  { agent: 'Yoda', action: 'coordinating agents', icon: '📡', phase: 'comms' },
  { agent: 'Logic', action: 'compiling brick context', icon: '🧱', phase: 'process' },
  { agent: 'Logic', action: 'reasoning through query', icon: '💭', phase: 'process' },
];

// Pixel art mini database icon
const PixelDB = () => (
  <svg width="16" height="16" viewBox="0 0 8 8" className="inline-block">
    <rect x="1" y="1" width="6" height="2" fill="#AEA36D" opacity="0.8" />
    <rect x="1" y="3" width="6" height="2" fill="#AEA36D" opacity="0.5" />
    <rect x="1" y="5" width="6" height="2" fill="#AEA36D" opacity="0.3" />
    <rect x="0" y="0" width="1" height="8" fill="#AEA36D" opacity="0.2" />
    <rect x="7" y="0" width="1" height="8" fill="#AEA36D" opacity="0.2" />
  </svg>
);

// Data packet flowing between agent and DB
const DataPacket = ({ delay = 0 }) => (
  <motion.div
    className="absolute h-px"
    style={{ width: '100%', top: '50%' }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay }}
  >
    <motion.div
      className="w-1.5 h-1.5 rounded-full bg-[#AEA36D]"
      style={{ filter: 'blur(0.5px)' }}
      animate={{ x: [0, 120, 0], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 2, delay, repeat: Infinity, ease: 'linear' }}
    />
  </motion.div>
);

// Single agent sprite
const AgentSprite = ({ agent, isActive, size = 'sm' }) => {
  const agentData = AGENTS.find(a => a.name === agent) || AGENTS[0];
  const px = size === 'sm' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm';

  return (
    <motion.div
      className={`${px} rounded-sm flex items-center justify-center relative`}
      style={{
        background: isActive ? `${agentData.color}30` : 'transparent',
        border: isActive ? `1px solid ${agentData.color}50` : '1px solid transparent',
        imageRendering: 'pixelated',
      }}
      animate={isActive ? { y: [0, -2, 0], scale: [1, 1.1, 1] } : {}}
      transition={{ duration: 0.6, repeat: Infinity }}
    >
      <span style={{ fontSize: size === 'sm' ? '12px' : '16px' }}>{agentData.emoji}</span>
      {isActive && (
        <motion.div
          className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
          style={{ background: agentData.color }}
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 0.5, repeat: Infinity }}
        />
      )}
    </motion.div>
  );
};

// The agent scene — visual workspace
const AgentScene = ({ currentActivity, elapsed, isReal }) => {
  const agentData = AGENTS.find(a => a.name === currentActivity?.agent) || AGENTS[0];

  return (
    <div className="relative w-full h-20 overflow-hidden rounded-lg bg-black/40 border border-white/5">
      {/* Scanline effect */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
        }}
      />

      {/* Grid floor */}
      <div
        className="absolute bottom-0 left-0 right-0 h-8 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(rgba(174,163,109,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(174,163,109,0.3) 1px, transparent 1px)',
          backgroundSize: '16px 16px',
          perspective: '100px',
          transform: 'rotateX(45deg)',
          transformOrigin: 'bottom',
        }}
      />

      {/* Database tower (right side) */}
      <div className="absolute right-4 top-2 flex flex-col items-center gap-0.5">
        <motion.div
          animate={{ opacity: currentActivity?.phase === 'db' ? [0.5, 1, 0.5] : 0.3 }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <PixelDB />
        </motion.div>
        <span className="text-[7px] text-[#AEA36D]/40 font-mono">SUPABASE</span>
      </div>

      {/* Active agent (center-left) */}
      <div className="absolute left-6 top-3 flex items-center gap-3">
        <AgentSprite agent={currentActivity?.agent || 'Yoda'} isActive={true} size="md" />
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            <span
              className="text-[9px] font-mono font-bold"
              style={{ color: agentData.color }}
            >
              {currentActivity?.agent || 'Yoda'}
            </span>
            {/* LIVE indicator when showing real pipeline data */}
            {isReal && (
              <motion.span
                className="text-[7px] font-mono px-1 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                LIVE
              </motion.span>
            )}
          </div>
          <motion.span
            key={currentActivity?.action}
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-[10px] text-white/50 font-mono"
          >
            {currentActivity?.action || 'initializing...'}
          </motion.span>
        </div>
      </div>

      {/* Data packets flowing between agent and DB */}
      {currentActivity?.phase === 'db' && (
        <div className="absolute top-6 left-20 right-12">
          <DataPacket delay={0} />
          <DataPacket delay={0.7} />
          <DataPacket delay={1.4} />
        </div>
      )}

      {/* Communication waves (comms phase) */}
      {currentActivity?.phase === 'comms' && (
        <div className="absolute top-4 right-20">
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 rounded-full border border-purple-500/30"
              animate={{ scale: [1, 2.5], opacity: [0.6, 0] }}
              transition={{ duration: 1.5, delay: i * 0.4, repeat: Infinity }}
            />
          ))}
        </div>
      )}

      {/* Processing gear (process phase) */}
      {currentActivity?.phase === 'process' && (
        <motion.div
          className="absolute right-20 top-4 text-[#AEA36D]/30"
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        >
          <span className="text-lg">⚙</span>
        </motion.div>
      )}

      {/* Background agents (idle) */}
      <div className="absolute bottom-1.5 left-2 flex gap-1">
        {AGENTS.filter(a => a.name !== currentActivity?.agent).slice(0, 3).map((a, i) => (
          <motion.div
            key={a.name}
            className="w-4 h-4 rounded-sm flex items-center justify-center opacity-20"
            animate={{ y: [0, -1, 0] }}
            transition={{ duration: 2, delay: i * 0.5, repeat: Infinity }}
          >
            <span style={{ fontSize: '8px' }}>{a.emoji}</span>
          </motion.div>
        ))}
      </div>

      {/* Elapsed timer */}
      <div className="absolute bottom-1 right-2 text-[8px] text-white/15 font-mono">
        {elapsed}s
      </div>
    </div>
  );
};

// Activity log — scrolling terminal
const ActivityLog = ({ activities }) => (
  <div className="space-y-0.5 max-h-16 overflow-hidden">
    <AnimatePresence mode="popLayout">
      {activities.slice(-3).map((activity, i) => {
        const isLatest = i === Math.min(2, activities.length - 1);
        const agentData = AGENTS.find(a => a.name === activity.agent) || AGENTS[0];

        return (
          <motion.div
            key={activity.id || `${activity.action}-${i}`}
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: isLatest ? 1 : 0.3, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-1.5 text-[10px] font-mono"
          >
            {/* Real event indicator */}
            {activity.isReal && (
              <span className="w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
            )}
            <span style={{ color: agentData.color }}>{activity.icon}</span>
            <span style={{ color: `${agentData.color}99` }}>{activity.agent}</span>
            <span className="text-white/30">→</span>
            <motion.span
              className={activity.isReal ? 'text-white/70' : 'text-white/40'}
              animate={isLatest ? { opacity: [0.5, 1, 0.5] } : {}}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              {activity.action}
            </motion.span>
            {isLatest && (
              <motion.span
                className="text-[#AEA36D]"
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.8, repeat: Infinity }}
              >
                ▌
              </motion.span>
            )}
          </motion.div>
        );
      })}
    </AnimatePresence>
  </div>
);

/**
 * AgentThinkingAnimation — Main exported component
 *
 * Subscribes to Realtime 'progress' events from avengers.js.
 * Real events immediately replace filler. Filler plays between real events.
 *
 * @param {string} prompt - The user's prompt (pinned at top)
 * @param {string} commandId - The agent_commands UUID (filters progress events)
 * @param {string} mode - 'bubble' (compact) or 'fullscreen' (expanded panel)
 * @param {string} brickHint - Which brick domain is active
 */
const AgentThinkingAnimation = ({ prompt, commandId, mode = 'bubble', brickHint }) => {
  const [elapsed, setElapsed] = useState(0);
  const [activityLog, setActivityLog] = useState([]);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [hasRealEvents, setHasRealEvents] = useState(false);
  const startTimeRef = useRef(Date.now());
  const fillerIndexRef = useRef(0);
  const fillerTimerRef = useRef(null);
  const channelRef = useRef(null);

  // Convert a real progress event into an activity object
  const progressToActivity = useCallback((payload) => {
    const agentName = payload.agent_name || 'Logic';
    return {
      id: `real-${payload.step}-${payload.ts}`,
      agent: agentName,
      action: payload.detail || payload.step.replace(/_/g, ' '),
      icon: STEP_ICONS[payload.step] || '📡',
      phase: STEP_TO_PHASE[payload.step] || 'process',
      isReal: true,
    };
  }, []);

  // Push a filler activity (ambient animation between real events)
  const pushFiller = useCallback(() => {
    const filler = FILLER_ACTIVITIES[fillerIndexRef.current % FILLER_ACTIVITIES.length];
    fillerIndexRef.current++;
    const activity = {
      ...filler,
      id: `filler-${Date.now()}`,
      isReal: false,
    };
    setCurrentActivity(activity);
    setActivityLog(prev => [...prev.slice(-8), activity]);
  }, []);

  // Subscribe to Realtime progress events
  useEffect(() => {
    if (!commandId) return;

    const channel = supabase.channel('agent-progress-' + commandId.substring(0, 8))
      .on('broadcast', { event: 'progress' }, (payload) => {
        const data = payload.payload;
        if (data.command_id !== commandId) return;

        console.log('[ThinkingAnim] Real progress:', data.step, data.detail);
        setHasRealEvents(true);

        const activity = progressToActivity(data);
        setCurrentActivity(activity);
        setActivityLog(prev => [...prev.slice(-8), activity]);

        // Reset filler timer — give real event breathing room
        if (fillerTimerRef.current) clearInterval(fillerTimerRef.current);
        fillerTimerRef.current = setInterval(pushFiller, 3000);
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [commandId, progressToActivity, pushFiller]);

  // Also subscribe to the main agent-responses channel (same one avengers broadcasts on)
  useEffect(() => {
    if (!commandId) return;

    const channel = supabase.channel('agent-responses')
      .on('broadcast', { event: 'progress' }, (payload) => {
        const data = payload.payload;
        if (data.command_id !== commandId) return;

        console.log('[ThinkingAnim] Real progress (main channel):', data.step, data.detail);
        setHasRealEvents(true);

        const activity = progressToActivity(data);
        setCurrentActivity(activity);
        setActivityLog(prev => [...prev.slice(-8), activity]);

        // Reset filler timer
        if (fillerTimerRef.current) clearInterval(fillerTimerRef.current);
        fillerTimerRef.current = setInterval(pushFiller, 3000);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [commandId, progressToActivity, pushFiller]);

  // Start filler animation immediately (real events override when they arrive)
  useEffect(() => {
    // Push initial filler
    pushFiller();

    // Continue filling every 2.5s until real events take over
    fillerTimerRef.current = setInterval(pushFiller, 2500);

    return () => {
      if (fillerTimerRef.current) clearInterval(fillerTimerRef.current);
    };
  }, [pushFiller]);

  // Elapsed timer
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const displayActivity = currentActivity || {
    agent: 'Yoda', action: 'initializing...', icon: '📡', phase: 'comms', isReal: false,
  };

  if (mode === 'bubble') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        {/* User prompt pinned */}
        <div className="flex items-start gap-2 text-xs font-mono">
          <span className="text-[#AEA36D] shrink-0">❯</span>
          <span className="text-white/60 line-clamp-2">{prompt}</span>
        </div>

        <div className="border-t border-white/5" />

        {/* Agent scene */}
        <AgentScene currentActivity={displayActivity} elapsed={elapsed} isReal={hasRealEvents} />

        {/* Activity log */}
        <ActivityLog activities={activityLog} />
      </motion.div>
    );
  }

  // Fullscreen / panel mode
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3 px-1"
    >
      {/* User prompt pinned at top */}
      <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5">
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-white/30 font-mono">YOUR PROMPT</div>
          {hasRealEvents && (
            <motion.div
              className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
              animate={{ opacity: [1, 0.6, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              LIVE PIPELINE
            </motion.div>
          )}
        </div>
        <p className="text-sm text-white/80 line-clamp-3 mt-1">{prompt}</p>
      </div>

      {/* Agent scene */}
      <AgentScene currentActivity={displayActivity} elapsed={elapsed} isReal={hasRealEvents} />

      {/* Activity log */}
      <div className="pl-2">
        <div className="text-[9px] text-white/20 font-mono mb-1 tracking-widest">
          {hasRealEvents ? 'PIPELINE ACTIVITY' : 'AGENT ACTIVITY'}
        </div>
        <ActivityLog activities={activityLog} />
      </div>

      {/* Phase indicator */}
      <div className="flex items-center justify-center gap-4 pt-1">
        {['db', 'comms', 'process'].map(phase => (
          <div key={phase} className="flex items-center gap-1.5">
            <motion.div
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: displayActivity?.phase === phase ? '#AEA36D' : 'rgba(255,255,255,0.1)',
              }}
              animate={displayActivity?.phase === phase ? {
                scale: [1, 1.5, 1],
                opacity: [1, 0.5, 1],
              } : {}}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className={`text-[9px] font-mono ${displayActivity?.phase === phase ? 'text-[#AEA36D]/70' : 'text-white/15'}`}>
              {phase === 'db' ? 'DATABASE' : phase === 'comms' ? 'AGENTS' : 'THINKING'}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default AgentThinkingAnimation;
